// Shared presentation helpers for the custom-invoices surfaces (list /
// detail / print). Custom invoices are the reseller→customer invoices
// issued by the custom-billing engine — distinct from the Stripe
// subscription invoices shown on /billing/invoices.
import type { CustomInvoiceStatus } from '@/api/customBilling';
import { cn } from '@/lib/cn';

export const INVOICE_STATUS_STYLES: Record<CustomInvoiceStatus, { label: string; className: string }> = {
  draft:  { label: 'Draft',  className: 'bg-slate-50 text-slate-600 border-slate-200' },
  issued: { label: 'Issued', className: 'bg-sky-50 text-sky-700 border-sky-200' },
  paid:   { label: 'Paid',   className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  failed: { label: 'Failed', className: 'bg-red-50 text-red-700 border-red-200' },
  void:   { label: 'Void',   className: 'bg-slate-50 text-slate-400 border-slate-200 line-through' },
};

export function InvoiceStatusPill({ status, className }: { status: CustomInvoiceStatus; className?: string }) {
  const s = INVOICE_STATUS_STYLES[status] ?? INVOICE_STATUS_STYLES.draft;
  return (
    <span className={cn(
      'inline-flex items-center rounded border px-1.5 py-0.5 text-[11px] font-medium uppercase tracking-wider leading-none',
      s.className,
      className,
    )}>
      {s.label}
    </span>
  );
}

export function formatMoney(cents: number): string {
  return `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
}

/** For Postgres `date` columns (YYYY-MM-DD) — formats WITHOUT constructing a
 *  Date from the bare string, which would be parsed as UTC midnight and can
 *  render as the previous day in negative-offset timezones. */
export function formatDateOnly(ymd: string | null): string {
  if (!ymd) return '—';
  const [y, m, d] = ymd.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return ymd;
  return new Date(y, m - 1, d).toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
  });
}

/** For timestamptz ISO strings (issued_at / paid_at / due_at). */
export function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
  });
}

/** numeric(14,4) quantities arrive as strings like "5.0000" — trim the
 *  noise but keep real fractions (e.g. storage GB "12.3400" → "12.34"). */
export function formatQty(q: string | number): string {
  const n = typeof q === 'number' ? q : parseFloat(q);
  if (!Number.isFinite(n)) return String(q);
  return n.toLocaleString('en-US', { maximumFractionDigits: 4 });
}
